import { IonLabel, IonSegment, IonSegmentButton } from '@ionic/react';
import { FileImageIcon } from '@phosphor-icons/react';
import React from 'react';

import useDocumentacionHandler from '../../../hooks/Reintegro/Documentacion/useDocumentacionHandler';
import Alert from '../../UI/Alert';
import { iconSiniestroProps } from '../../../pages/siniestro';
import Button from '../../UI/Button';
import CameraActionSheet from '../../UI/CameraActionSheet';
import FileUploader from '../../UI/FileUploader';
import FormHeader from '../../UI/FormHeader';
import Gallery from '../../UI/Gallery';
import Spinner from '../../UI/Spinner';
import './Documentacion.scss';
import { DocumentacionProps } from './types';

const Documentacion: React.FC<DocumentacionProps> = ({
  onNext,
  buttonLabel = 'Siguiente',
}) => {
  const {
    documentacion,
    cbuType,
    setCBU_Type,
    showCameraOptions,
    galleryUrl,
    showGallery,
    isValidating,
    getFileUploaderProps,
    closeCameraOptions,
    closeGallery,
    uploadAlertState,
    hideUploadAlert,
    handlePhoto,
    handleGallery,
    handlePDFClick,
    parseURL,
    basic,
    cbu,
    tercero,
    getUploadedFiles,
  } = useDocumentacionHandler();

  const isInitialLoading = isValidating && !documentacion;

  if (isInitialLoading) {
    return (
      <div className="documentacion ion-padding">
        <Spinner fullscreen transparent />
      </div>
    );
  }

  const renderDynamicUploaders = (element: (typeof cbu)[number]) => {
    const uploadedFiles = getUploadedFiles(element);

    return (
      <React.Fragment key={element.key}>
        {uploadedFiles.map((_, fileIndex) => (
          <FileUploader
            key={`${element.key}-${fileIndex}`}
            {...getFileUploaderProps(element, {
              mode: 'view',
              fileIndex,
              label: `${element.label} ${fileIndex + 1}`,
            })}
          />
        ))}

        <FileUploader
          key={`${element.key}-new`}
          {...getFileUploaderProps(element, {
            emptyUpload: true,
            label: uploadedFiles.length ? `otro ${element.label}` : element.label,
          })}
        />
      </React.Fragment>
    );
  };

  return (
    <div className="documentacion ion-padding">
      <FormHeader
        icon={<FileImageIcon {...iconSiniestroProps} />}
        title="Documentación"
        subtitle="Documentación del caso"
      />

      <div className="documentacion__form-content">
        {basic.map((element, index) => (
          <FileUploader key={index} {...getFileUploaderProps(element)} />
        ))}

        <div className="documentacion__cbu-title">Comprobante de CBU</div>

        <IonSegment
          value={cbuType}
          onIonChange={(e) => setCBU_Type(e.detail.value as 'propio' | 'tercero')}
          mode="md"
          className="ion-no-margin"
        >
          <IonSegmentButton value="propio">
            <IonLabel>Cuenta propia</IonLabel>
          </IonSegmentButton>
          <IonSegmentButton value="tercero">
            <IonLabel>Cuenta de tercero</IonLabel>
          </IonSegmentButton>
        </IonSegment>

        <div className="ion-padding-top">
          {cbuType === 'propio' &&
            cbu.map((element) => renderDynamicUploaders(element))}

          {cbuType === 'tercero' &&
            tercero.map((element) => renderDynamicUploaders(element))}

          <Button noMarginTop onClick={onNext}>
            {buttonLabel}
          </Button>
        </div>
      </div>

      {showCameraOptions && (
        <CameraActionSheet
          showCameraOptions={showCameraOptions}
          setShowCameraOptions={closeCameraOptions}
          takePhoto={handlePhoto}
          selectFromGallery={handleGallery}
          uploadPDF={handlePDFClick}
        />
      )}

      {showGallery && (
        <Gallery
          isOpen={showGallery}
          onClose={closeGallery}
          fileUrl={parseURL(galleryUrl ?? '')}
          isValidating={isValidating}
          title='Documento'
        />
      )}

      <Alert
        isOpen={uploadAlertState.isOpen}
        header={uploadAlertState.header}
        message={uploadAlertState.message}
        buttons={uploadAlertState.buttons}
        onDidDismiss={hideUploadAlert}
      />
    </div>
  );
};

export default React.memo(Documentacion);
